import {
  AllCountriesDataProps,
  ErrorBoundary,
  Loading,
  LookingForSomethingElse,
  SectionHeader,
  SubscriptionType,
  getCountryMappings,
  publishEvent,
  useRefetch
} from '@uma/react-commons/dist/index';
import React, { useEffect, useMemo, useState } from 'react';
import { FeatureFlagProps } from 'src/App';
import { Content, Header } from 'src/App/styles';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import GET_SUBSCRIPTIONS from 'src/graphql/getSubscriptions';
import { Link, Section } from 'src/styles/styles';
import {
  HPStoreSubscriptionHyperLinkClicked,
  ScreenDisplayed,
  getModuleDisplayedError,
  screenName,
  screenPath
} from 'src/utils/analytics';
import { URLS } from 'src/utils/urls';
import Cards from './Cards';
import {
  ContentSectionNoSubscriptions,
  HPStoreSubscriptionsInfo,
  IconGhost,
  NoSubscriptions
} from './styles';

const Subscriptions = (featureFlagProps: FeatureFlagProps): JSX.Element => {
  const operation = 'GET_SUBSCRIPTIONS';

  const { translate, northboundAPIs, logger } = useDependencyManagerContext();

  const useQuery = northboundAPIs?.v1?.graphql.reactTools.useQuery;

  const { loading, error, data, refetch } = useQuery(GET_SUBSCRIPTIONS);

  const { isRefetching, handleRefetch } = useRefetch({
    refetch,
    error,
    operation
  });

  const {
    isLoading: isFeatureFlagLoading,
    originalFeedbackExperience,
    findMoreOfYourPlansOnlyForUs
  } = featureFlagProps;

  const [seeFindMoreOfYourPlansLink, setSeeFindMoreOfYourPlansLink] = useState<
    boolean | string | null
  >(false);

  useEffect(() => {
    const fetchCountryMappings = async () => {
      try {
        const result = await getCountryMappings(
          AllCountriesDataProps.seeFindMoreOfYourPlansLink
        );
        setSeeFindMoreOfYourPlansLink(result);
      } catch (error) {
        logger.error('Error fetching country mappings:', error);
        setSeeFindMoreOfYourPlansLink(URLS.HPStoreSubscriptions);
      }
    };
    fetchCountryMappings();
  }, []);

  const memoizedOriginalFeedbackExperience = useMemo(
    () => originalFeedbackExperience,
    [originalFeedbackExperience]
  );

  const memoizedIsOriginalFeedbackExperienceLoading = useMemo(
    () => isFeatureFlagLoading,
    [isFeatureFlagLoading]
  );

  const showOriginalFeedbackExperience =
    memoizedOriginalFeedbackExperience &&
    !memoizedIsOriginalFeedbackExperienceLoading;

  const subscriptionsLength = data?.account?.subscriptions?.length || 0;

  const hpInstantInkLength = data?.account?.subscriptions?.filter(
    (item: { type: string }) => item.type == SubscriptionType.INSTANT_INK
  )?.length;

  const hpAllInLength = data?.account?.subscriptions?.filter(
    (item: { type: string }) => item.type == SubscriptionType.HP_ALL_IN
  )?.length;

  useEffect(() => {
    if (
      subscriptionsLength != undefined &&
      hpInstantInkLength != undefined &&
      hpAllInLength != undefined
    ) {
      publishEvent(
        ScreenDisplayed(subscriptionsLength, hpInstantInkLength, hpAllInLength)
      );
    }
  }, [hpAllInLength, hpInstantInkLength, subscriptionsLength]);

  const shouldShowHPStoreSubscriptionsInfo =
    !findMoreOfYourPlansOnlyForUs || !!seeFindMoreOfYourPlansLink;

  const isValidFindMoreOfYourPlansLink =
    typeof seeFindMoreOfYourPlansLink === 'string' &&
    seeFindMoreOfYourPlansLink.trim() !== '';

  const renderFindMoreOfYourPlansLink = (link: string) => {
    const htmlString = translate(
      'subscriptions.findMrePlansServicesStoreSubscriptions',
      'Find more of your plans & services in your {{tagReplaceStart}}HP Store subscriptions{{tagReplaceEnd}}.'
    );

    const [start, middle, end] = htmlString.split(
      /{{\/?tagReplaceStart}}|{{\/?tagReplaceEnd}}/
    );

    return (
      <p className="body">
        {start}
        <Link
          data-testid="hp-store-subscriptions-link"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            publishEvent(HPStoreSubscriptionHyperLinkClicked(link))
          }
        >
          {middle}
        </Link>
        {end}
      </p>
    );
  };

  const showCards = !loading && !error && subscriptionsLength > 0;
  const showEmpty =
    !loading && !error && !!data?.account && subscriptionsLength === 0;
  const showStoreInfo =
    !loading && !isFeatureFlagLoading && shouldShowHPStoreSubscriptionsInfo;

  return (
    <>
      <Header>
        <SectionHeader
          title={translate('menu.subscriptions', 'Subscriptions & services')}
          subtitle={translate(
            'subscriptions.viewOrManageCurrentPastSubscriptions',
            'View or manage your current and past HP subscriptions'
          )}
        />
      </Header>

      <Content data-testid="content-subscriptions">
        {loading && (
          <Section
            border="outlined"
            background="filled"
          >
            <Loading />
          </Section>
        )}
        {error && (
          <ErrorBoundary
            analyticsEvent={getModuleDisplayedError(error?.message)}
            isRounded
            showOutline
            loading={isRefetching}
            onRetry={handleRefetch}
            errorInfo={{
              error,
              context: 'Subscriptions',
              operation
            }}
          />
        )}
        {showCards && <Cards subscriptions={data?.account?.subscriptions} />}
        {showEmpty && (
          <Section
            border="outlined"
            background="filled"
            data-testid="section-no-subscriptions"
          >
            <ContentSectionNoSubscriptions>
              <IconGhost size={40} />
              <NoSubscriptions>
                {`${translate(
                  'subscriptions.noSubscriptionsYet',
                  'No subscriptions yet!'
                )}`}
              </NoSubscriptions>
            </ContentSectionNoSubscriptions>
          </Section>
        )}
      </Content>
      {showStoreInfo && (
        <HPStoreSubscriptionsInfo>
          {renderFindMoreOfYourPlansLink(
            isValidFindMoreOfYourPlansLink
              ? seeFindMoreOfYourPlansLink
              : URLS.HPStoreSubscriptions
          )}
        </HPStoreSubscriptionsInfo>
      )}
      {showOriginalFeedbackExperience && (
        <LookingForSomethingElse
          analytics={{
            screenName: screenName,
            screenPath: screenPath
          }}
        />
      )}
    </>
  );
};

export default Subscriptions;
